import React, { Component } from 'react';
import moment from 'moment';
import { DateTime } from 'luxon';
import { Typography, withStyles } from '@material-ui/core';
import TextField from '@mui/material/TextField';
import { MenuItem } from '@mui/material';
import { withSnackbar } from 'notistack';
import styles from './styles';
import './styles.scss';

class SimplifiedDatePicker extends Component {
  constructor(props) {
    super(props);

    const today = moment();
    // Convert startDate and endDate props to moment objects if they're provided
    const startDate = this.props.startDate
      ? moment.isMoment(this.props.startDate)
        ? this.props.startDate
        : moment(this.props.startDate)
      : null;
    const endDate = this.props.endDate
      ? moment.isMoment(this.props.endDate)
        ? this.props.endDate
        : moment(this.props.endDate)
      : null;

    this.state = {
      startDate: startDate,
      endDate: endDate,
      startDateError: '',
      endDateError: ''
    };
  }

  // Helper function to format time with AM/PM
  formatTime(hour, minute, period) {
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;
    const formattedMinute = minute < 10 ? '0' + minute : minute;
    return `${displayHour}:${formattedMinute} ${period}`;
  }

  generateTimeOptions() {
    const hours = Array.from({ length: 12 }, (_, i) => i + 1);
    const minutes = [0];
    const anytimeOption = 'anytime';

    const amTimes = hours.flatMap((hour) =>
      minutes.map((minute) => ({
        value: `${this.formatTime(hour, minute, 'AM')}`,
        label: this.formatTime(hour, minute, 'AM')
      }))
    );

    const pmTimes = hours.flatMap((hour) =>
      minutes.map((minute) => ({
        value: `${this.formatTime(hour, minute, 'PM')}`,
        label: this.formatTime(hour, minute, 'PM')
      }))
    );

    return [anytimeOption, ...amTimes, ...pmTimes];
  }

  validateStartDate = (startDate) => {
    const twoMonthsAgo = moment().subtract(2, 'months').startOf('day');
    let startDateError = '';

    if (startDate.isBefore(twoMonthsAgo, 'day')) {
      startDateError = 'Start date cannot be more than 2 months in the past';
    }

    return { startDateError };
  };

  validateEndDate = (endDate) => {
    const twoMonthsAgo = moment().subtract(2, 'months').startOf('day');
    let endDateError = '';

    if (endDate.isBefore(twoMonthsAgo, 'day')) {
      endDateError = 'End date cannot be more than 2 months in the past';
    }

    return { endDateError };
  };

  handleStartDateChange = (event) => {
    const selectedDate = moment(event.target.value).startOf('day');
    const { endDate } = this.state;

    const { startDateError } = this.validateStartDate(selectedDate);

    // Only reset endDate if the new start date is after the current end date
    const shouldResetEndDate = endDate && selectedDate.isAfter(endDate, 'day');
    const newEndDate = shouldResetEndDate ? null : endDate;

    this.setState(
      {
        startDate: selectedDate,
        endDate: newEndDate, // Only reset if start date is after end date
        startDateError,
        endDateError: shouldResetEndDate ? '' : this.state.endDateError // Clear end date error if reset
      },
      () => {
        // Only call onDateChange if there's no error
        if (!startDateError && this.props.onDateChange) {
          // Convert to DateTime at start of day (automatically uses local timezone)
          const startDateTime = DateTime.fromJSDate(
            selectedDate.toDate()
          ).startOf('day');
          // If endDate exists and is valid, use it, otherwise use start date as end date
          const endDateTime = newEndDate
            ? DateTime.fromJSDate(newEndDate.toDate()).endOf('day')
            : startDateTime.endOf('day');

          this.props.onDateChange(startDateTime, endDateTime);
        }
      }
    );
  };

  handleEndDateChange = (event) => {
    const selectedDate = moment(event.target.value).startOf('day');
    const { startDate } = this.state;

    if (!startDate) {
      // Start date should be selected first
      this.props.enqueueSnackbar('Please select a start date first', {
        variant: 'error'
      });
      return;
    }

    // Check if end date is before start date
    if (selectedDate.isBefore(startDate, 'day')) {
      this.props.enqueueSnackbar(
        'End date cannot be selected before start date',
        {
          variant: 'error'
        }
      );
      return; // Don't update the state if invalid
    }

    const { endDateError } = this.validateEndDate(selectedDate);

    this.setState(
      {
        endDate: selectedDate,
        endDateError
      },
      () => {
        // Only call onDateChange if there are no errors and both dates are selected
        if (
          !this.state.startDateError &&
          !endDateError &&
          this.props.onDateChange &&
          startDate
        ) {
          // Convert to DateTime with proper day boundaries (automatically uses local timezone)
          const startDateTime = DateTime.fromJSDate(startDate.toDate()).startOf(
            'day'
          );
          const endDateTime = DateTime.fromJSDate(selectedDate.toDate()).endOf(
            'day'
          );

          this.props.onDateChange(startDateTime, endDateTime);
        }
      }
    );
  };

  componentDidUpdate(prevProps) {
    if (prevProps.isSearch !== this.props.isSearch) {
      if (this.props.isSearch === false) {
        const startDate = this.props.startDate
          ? moment.isMoment(this.props.startDate)
            ? this.props.startDate
            : moment(this.props.startDate)
          : null;
        const endDate = this.props.endDate
          ? moment.isMoment(this.props.endDate)
            ? this.props.endDate
            : moment(this.props.endDate)
          : null;
        this.setState({
          startDate: startDate,
          endDate: endDate,
          startDateError: '',
          endDateError: ''
        });
      }
    }
    // Also update if startDate or endDate props change
    if (
      prevProps.startDate !== this.props.startDate ||
      prevProps.endDate !== this.props.endDate
    ) {
      const startDate = this.props.startDate
        ? moment.isMoment(this.props.startDate)
          ? this.props.startDate
          : moment(this.props.startDate)
        : null;
      const endDate = this.props.endDate
        ? moment.isMoment(this.props.endDate)
          ? this.props.endDate
          : moment(this.props.endDate)
        : null;
      this.setState({
        startDate: startDate,
        endDate: endDate
      });
    }
  }

  render() {
    const { startDate, endDate, startDateError, endDateError } = this.state;
    const { showTime, classes } = this.props;
    const timeOptions = this.generateTimeOptions();

    // Calculate minimum date (2 months ago)
    const minDate = moment().subtract(2, 'months').format('YYYY-MM-DD');
    const isEndDateDisabled = !startDate; // Disable end date if no start date is selected

    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <TextField
          id="start-date-picker"
          type="date"
          label="Start Date"
          placeholder=""
          value={startDate ? startDate.format('YYYY-MM-DD') : ''}
          onChange={this.handleStartDateChange}
          variant="standard"
          style={{ width: 140 }}
          className={classes?.textField}
          inputProps={{
            min: minDate
          }}
          InputLabelProps={{
            shrink: true
          }}
          error={!!startDateError}
          helperText={startDateError || ' '} // Always reserve space for helper text
          sx={{
            input: { color: this.props.isSearch ? 'blue' : 'primary' },
            '& .MuiFormHelperText-root': {
              color: 'error.main',
              minHeight: '20px'
            }
          }}
        />

        <Typography
          className="calendar-text"
          variant="h6"
          style={{
            margin: '0',
            alignSelf: 'center',
            marginTop: '15px', // Adjust to align with input fields
            fontSize: '16px'
          }}>
          To
        </Typography>

        <TextField
          id="end-date-picker"
          type="date"
          label="End Date"
          placeholder=""
          value={endDate ? endDate.format('YYYY-MM-DD') : ''}
          onChange={this.handleEndDateChange}
          variant="standard"
          style={{ width: 140 }}
          className={classes?.textField}
          disabled={isEndDateDisabled}
          inputProps={{
            min: startDate ? startDate.format('YYYY-MM-DD') : minDate
          }}
          InputLabelProps={{
            shrink: true
          }}
          error={!!endDateError}
          helperText={isEndDateDisabled ? ' ' : endDateError || ' '} // Always reserve space
          sx={{
            input: { color: this.props.isSearch ? 'blue' : 'inherit' },
            '& .MuiFormHelperText-root': {
              color: isEndDateDisabled ? 'text.secondary' : 'error.main',
              minHeight: '20px'
            },
            '& .MuiInputBase-input:disabled': {
              backgroundColor: '#f5f5f5'
            }
          }}
        />

        {showTime && (
          <TextField
            id="start-time"
            style={{ width: 160 }}
            label="Time"
            select
            variant="standard"
            color="primary"
            value={this.props.selectedTime || 'anytime'}
            onChange={this.props.onTimeChange}
            className={classes?.textField}
            InputLabelProps={{
              shrink: true
            }}
            // Add helper text space to match date fields
            helperText=" "
            sx={{
              '& .MuiFormHelperText-root': {
                minHeight: '20px'
              }
            }}>
            {timeOptions.map((option, index) => (
              <MenuItem key={index} value={option.value || option}>
                {option.label || option}
              </MenuItem>
            ))}
          </TextField>
        )}
      </div>
    );
  }
}

export default withSnackbar(withStyles(styles)(SimplifiedDatePicker));
